import { useNavigation } from "@react-navigation/native";
import { BlurView } from "expo-blur";
import { useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";
import { PermissionsManager } from "../../utilities/permissions";

export interface PermissionGuardProps {
  /**
   * token for applicable ManagedPermissions ie. [PermissionToken.Camera]
   */
  permissions: string[];
  /**
   * show children behind modal or hide completely
   */
  allowScreen?: boolean;
  children?: React.ReactNode;
}

/**
 * Pop up permissions modal if permissions are not granted
 * @param allowScreen show screen behind modal
 */
export function PermissionGuard({
  permissions,
  allowScreen = true,
  children,
}: PermissionGuardProps) {
  const navigation = useNavigation<any>();

  const [isBlocked, setIsBlocked] = useState<boolean>(checkIsBlocked());

  function checkIsBlocked() {
    return permissions.some((permission) => {
      return !PermissionsManager.hasPermission(permission);
    });
  }

  function refreshBlockedState() {
    navigation.pop();
    const newIsBlocked = checkIsBlocked();
    setIsBlocked(newIsBlocked);
  }

  useEffect(() => {
    // listen for changes to all required permissions
    const cleanupCallbacks = permissions.map((permission) => {
      const managedPermission =
        PermissionsManager.getManagedPermission(permission);
      return managedPermission.addOnChangeListener(refreshBlockedState);
    });

    return () => {
      cleanupCallbacks.forEach((cleanup) => {
        cleanup();
      });
    };
  }, [permissions]);

  useEffect(() => {
    if (isBlocked) {
      navigation.navigate("PermissionsModalRootScreen");
    }
  }, [isBlocked]);

  if (isBlocked) {
    if (allowScreen) {
      return (
        <View style={styles.fillScreen}>
          {children}
          <BlurView
            intensity={50}
            style={[styles.fillScreen, { position: "absolute", zIndex: 2 }]}
          />
        </View>
      );
    } else {
      return null;
    }
  }

  // render children unchanged if permissions are granted
  return <>{children}</>;
}

const styles = StyleSheet.create({
  fillScreen: {
    display: "flex",
    width: "100%",
    height: "100%",
  },
});
